import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import Header from '../components/Header';
import Button from '../components/Button';
import QualifierReport from '../components/QualifierReport';
import { getBookingDetails, getCounselingByTest } from '../services/api';
import { loadGoogleTagManager } from '../utils/googleTagManager';
import { colors } from '../theme';

const SuccessContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const StepContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 1.5rem;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
`;

const StepNumber = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${props => props.active ? colors.softPurple : 'white'};
  color: ${props => props.active ? 'white' : '#666'};
  border: 1px solid ${props => props.active ? colors.softPurple : '#ddd'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const StepLabel = styled.div`
  font-size: 0.9rem;
  color: ${props => props.active ? colors.softPurple : '#666'};
`;

const StepNav = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
`;

const SuccessSection = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  margin-bottom: 2rem;
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #4caf50;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 2rem;
`;

const SuccessTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const BookingDetails = styled.div`
  margin: 2rem 0;
  padding: 1.5rem;
  background-color: #f8f8f8;
  border-radius: 8px;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const DetailLabel = styled.div`
  font-weight: 500;
  color: #666;
`;

const DetailValue = styled.div`
  font-weight: 600;
`;

const ResultsSection = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const ButtonGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-top: 1.5rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  font-size: 1.2rem;
  color: #666;
`;

const TestSuccess = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [counselingBooked, setCounselingBooked] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [maxStep, setMaxStep] = useState(1);
  const [showQualifierReport, setShowQualifierReport] = useState(false);

  useEffect(() => {
    return loadGoogleTagManager();
  }, []);

  useEffect(() => {
    const fetchBookingDetails = async () => {
      try {
        setLoading(true);
        const data = await getBookingDetails(bookingId);
        setBooking(data);

        // If test is completed, allow access to step 2
        if (data?.test_status === 'completed') {
          setMaxStep(2);
        }
        
        // Check if counseling is already booked
        if (data?.test_booking_id) {
          try {
            const counselingData = await getCounselingByTest(data.test_booking_id);
            
            if (counselingData) {
              setMaxStep(3);
              setCounselingBooked(true);
            }
          } catch (error) {
            console.error('Error fetching counseling data:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching booking details:', error);
        toast.error('Failed to load booking details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchBookingDetails();
  }, [bookingId]);
  
  const handleStepClick = (step) => {
    // Only allow clicking on steps that are accessible
    if (step <= maxStep) {
      if (step === 2 && booking?.program_id === 1) {
        // If clicking on step 2, redirect to counseling booking
        navigate(`/book-counseling/${bookingId}`);
      } else {
        setCurrentStep(step);
      }
    }
  };
  
  // const handleNextStep = () => {
  //   if (currentStep < maxStep) {
  //     if (currentStep === 1 && maxStep >= 2) {
  //       // If moving from step 1 to step 2, redirect to counseling booking
  //       navigate(`/book-counseling/${bookingId}`);
  //     } else {
  //       setCurrentStep(currentStep + 1);
  //     }
  //   }
  // };
  
  // const handlePreviousStep = () => {
  //   if (currentStep > 1) {
  //     setCurrentStep(currentStep - 1);
  //   }
  // };
  
  const handleViewQualifierReport = () => {
    setShowQualifierReport(true);
  };

  const handleCloseQualifierReport = () => {
    setShowQualifierReport(false);
  };

  if (loading) {
    return (
      <SuccessContainer>
        <Header />
        <Content>
          <LoadingContainer>
            Loading booking details...
          </LoadingContainer>
        </Content>
      </SuccessContainer>
    );
  }
  
  return (
    <SuccessContainer>
      <Header />
      <Content>
        <StepContainer>
          <Step 
            clickable={true} 
            onClick={() => handleStepClick(1)}
          >
            <StepNumber active={currentStep === 1}>1</StepNumber>
            <StepLabel active={currentStep === 1}>Qualifier Test</StepLabel>
          </Step>
          <Step 
            clickable={maxStep >= 2} 
            onClick={() => handleStepClick(2)}
          >
            <StepNumber active={false}>2</StepNumber>
            <StepLabel active={false}>Secure Seat</StepLabel>
          </Step>
          <Step 
            clickable={maxStep >= 3} 
            onClick={() => handleStepClick(3)}
          >
            <StepNumber active={currentStep === 3}>3</StepNumber>
            <StepLabel active={currentStep === 3}>Complete Admission</StepLabel>
          </Step>
          <Step 
            clickable={maxStep >= 4} 
            onClick={() => handleStepClick(4)}
          >
            <StepNumber active={currentStep === 4}>4</StepNumber>
            <StepLabel active={currentStep === 4}>Join Program</StepLabel>
          </Step>
        </StepContainer>
        
        <SuccessSection>
          <SuccessIcon>✓</SuccessIcon>
          <SuccessTitle>Test Slot Booked Successfully!</SuccessTitle>
          <p style={{ color: '#666' }}>The test link will be shared on email 30 mins before the slot.</p>
          
          <BookingDetails>
            <DetailItem>
              <DetailLabel>Program</DetailLabel>
              <DetailValue>{booking?.program_name || "IIT Mandi - Minor Program in Fullstack AI Engineering"}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Test Date</DetailLabel>
              <DetailValue>
                {booking?.test_date
                  ? new Date(booking.test_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                  : ''}
              </DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Time Slot</DetailLabel>
              <DetailValue>{booking?.time_slot || '10:00 AM'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Test Status</DetailLabel>
              <DetailValue>
                {booking?.test_status
                  ? booking.test_status.charAt(0).toUpperCase() + booking.test_status.slice(1).toLowerCase()
                  : 'Scheduled'}
              </DetailValue>
            </DetailItem>
          </BookingDetails>

          {booking?.program_id === 2 && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-800 mb-3 flex items-center">

                Important Test Documents
              </h3>
              <p className="text-blue-700 mb-3 text-sm">Please review these documents before your test:</p>
              <div className="space-y-2 text-sm">
                <a href="/pdfs/AINPT - IITGN _ Platform Guidelines.pptx.pdf" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="block text-blue-700 hover:text-blue-900 hover:underline">
                  • Platform Guidelines
                </a>
                <a href="/pdfs/AINPT - IITGN (Structure & Sample QP).pdf" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="block text-blue-700 hover:text-blue-900 hover:underline">
                  • Test Structure & Sample Questions
                </a>
                <a href="/pdfs/AINPT - IITGN Test Preparation Guide.pdf" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="block text-blue-700 hover:text-blue-900 hover:underline">
                  • Test Preparation Guide
                </a>
              </div>
            </div>
          )}
          
        </SuccessSection>
        
        {booking?.program_id === 1 && (
        <ResultsSection>
          <SectionTitle>Test Results</SectionTitle>
          <p>After completing your test, you can view your results and proceed to the next step.</p>
          
          <ButtonGroup>
            <Button 
              variant={booking?.test_status === 'completed' ? 'primary' : 'secondary'}
              size="large" 
              disabled={booking?.test_status !== 'completed'}
              fullWidth
              onClick={handleViewQualifierReport}
            >
              QUALIFIER TEST REPORT
            </Button>
            
            <Button 
              variant={booking?.test_status === 'completed' ? 'primary' : 'secondary'} 
              size="large" 
              disabled={booking?.test_status !== 'completed'}
              fullWidth
              onClick={() => navigate(`/book-counseling/${bookingId}`)}
            >
              BOOK COUNSELING SESSION
            </Button>
          </ButtonGroup>
        </ResultsSection>

        )}
        
      </Content>

      {/* Qualifier Report Modal */}
      <QualifierReport 
        open={showQualifierReport} 
        onClose={handleCloseQualifierReport} 
        bookingId={bookingId} 
      />
    </SuccessContainer>
  );
};

export default TestSuccess;
